import React, { createContext, useContext, useState, useEffect } from 'react';
import { registerUserWithFirebase } from '../api/register.js';
import { login as apiLogin, loginWithFacebook as apiLoginWithFacebook, loginWithGithub as apiLoginWithGithub } from '../api/login.js';
import { getUserById } from '../api/users.js';
import apiClient from '../api/api.js';
import { auth } from '../Firebase.js';
import { signInAnonymously, onAuthStateChanged, signOut, signInWithEmailAndPassword } from 'firebase/auth';
import accountLinkingService from '../services/accountLinkingService.js';
import userTrackingService from '../services/userTrackingService.js';
import bigDataCloudService from '../services/bigDataCloudService.js';

// Create AuthContext
const AuthContext = createContext(null);

// AuthProvider component to wrap the app and provide user state
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [referrals, setReferrals] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [affiliateLink, setAffiliateLink] = useState(null);
  const [tokenBalance, setTokenBalance] = useState(undefined);
  const [walletBalance, setWalletBalance] = useState(undefined);

  // Check for existing token on app load
  useEffect(() => {
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    console.log('AuthContext - Initial token check:', token);
    console.log('AuthContext - Initial user data:', userData);

    if (token && userData) {
      try {
        const parsedUser = JSON.parse(userData);
        console.log('AuthContext - Setting user from localStorage:', parsedUser);
        console.log('AuthContext - Private code from localStorage:', parsedUser?.privateCode);
          setUser(parsedUser);
          userTrackingService.init(parsedUser.id);
        } catch (error) {
          console.error('Error parsing user data:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('user');
        }
      } else {
        console.log('AuthContext - No user data found, setting user to null');
      }
      setLoading(false);
    }, []);

  // Firebase auth state listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      console.log('AuthContext - Firebase auth state changed:', firebaseUser ? 'User signed in' : 'User signed out');

      if (firebaseUser) {
        console.log('AuthContext - Firebase user:', firebaseUser.uid, firebaseUser.isAnonymous ? '(Anonymous)' : '(Authenticated)');

        // If user is anonymous or authenticated, get ID token and authenticate with backend
        try {
          const idToken = await firebaseUser.getIdToken();

        // Call backend login-firebase endpoint
        const response = await apiClient.post('/api/auth/login-firebase', {}, {
          headers: {
            Authorization: `Bearer ${idToken}`,
            'X-Override-Auth': 'true' // Flag to bypass interceptor
          }
        });

          console.log('AuthContext - Backend login successful:', response.data.user);
          localStorage.setItem('token', response.data.token);
          localStorage.setItem('user', JSON.stringify(response.data.user));
          setUser(response.data.user);
          userTrackingService.init(response.data.user.id);

          // Generate and set affiliate link after successful login
          const affiliateRes = await generateAffiliateLink(response.data.user.id);
          if (affiliateRes.success) {
            setAffiliateLink(affiliateRes.affiliateLink);
            // Update user in localStorage with affiliate link
            const updatedUserWithAffiliate = { ...response.data.user, affiliateLink: affiliateRes.affiliateLink };
            localStorage.setItem('user', JSON.stringify(updatedUserWithAffiliate));
            setUser(updatedUserWithAffiliate);
          } else {
            console.warn('AuthContext - Failed to generate affiliate link after Firebase login:', affiliateRes.message);
          }

        } catch (error) {
          console.error('AuthContext - Backend login failed:', error);

          // Check if it's a 401 error (authentication failed)
          if (error.response && error.response.status === 401) {
            const errorMessage = error.response.data?.message || '';

            // If backend is in mock mode, don't sign out - just log the error
            if (errorMessage.includes('mock') || errorMessage.includes('Mock') ||
                errorMessage.includes('development') || errorMessage.includes('placeholder')) {
              console.warn('AuthContext - Backend appears to be in mock mode. Authentication skipped.');
              console.warn('AuthContext - To fix: Replace placeholder Firebase credentials with real ones.');
              // Don't sign out - let user stay logged in with Firebase
              // Instead, set user with mock data to avoid login error
              const privateCode = 'RM' + Math.random().toString(36).substring(2, 10).toUpperCase();
              const shortUid = firebaseUser.uid.substring(0, 8);
              const displayName = `User-${shortUid}-${privateCode.substring(2)}`;
              const email = firebaseUser.email || `user-${shortUid}@rapidmind.dev`;
              setUser({
                id: firebaseUser.uid,
                email,
                displayName,
                emailVerified: firebaseUser.emailVerified || false,
                planStatus: 'free',
                role: 'user',
                privateCode,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
              });
              return;
            }

            // For other 401 errors (expired token, etc.), sign out
            console.log('AuthContext - Authentication failed, signing out from Firebase');
            await signOut(auth);
          } else {
            // For other errors, also sign out
            console.log('AuthContext - Backend error, signing out from Firebase');
            await signOut(auth);
          }
        }
      } else {
        // User signed out from Firebase
        console.log('AuthContext - User signed out from Firebase');
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        userTrackingService.clearData();
      }
    });

    return () => unsubscribe();
  }, []);

  const login = async (email, password) => {
    try {
      console.log('🔐 [AuthContext] Starting login process...');
      console.log('🔐 [AuthContext] Email:', email);
      console.log('🔐 [AuthContext] Password provided:', password ? 'yes' : 'no');

      // Use the login function from login.js which handles Firebase authentication
      console.log('🔐 [AuthContext] Calling login API...');
      const response = await apiLogin(email, password);
      const data = response.data ? response.data : response;

      console.log('🔐 [AuthContext] Login API response received');
      console.log('🔐 [AuthContext] Response data:', data);
      console.log('🔐 [AuthContext] Login user data:', data.user);
      console.log('🔐 [AuthContext] Login private code:', data.user?.privateCode);

      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);

      // Start non-blocking operations in parallel for faster login
      console.log('🔐 [AuthContext] Starting non-blocking operations...');

      // Generate affiliate link asynchronously (non-blocking)
      generateAffiliateLink(data.user.id).then(affiliateRes => {
        if (affiliateRes.success) {
          setAffiliateLink(affiliateRes.affiliateLink);
          // Update user in localStorage with affiliate link
          const updatedUserWithAffiliate = { ...data.user, affiliateLink: affiliateRes.affiliateLink };
          localStorage.setItem('user', JSON.stringify(updatedUserWithAffiliate));
          setUser(updatedUserWithAffiliate);
          console.log('🔐 [AuthContext] Affiliate link generated successfully');
        } else {
          console.warn('AuthContext - Failed to generate affiliate link after login:', affiliateRes.message);
        }
      }).catch(error => {
        console.warn('AuthContext - Affiliate link generation failed:', error.message);
      });

      // Refresh user data asynchronously (non-blocking) - only essential data
      refreshUserData().then(() => {
        console.log('🔐 [AuthContext] User data refresh completed');
      }).catch(error => {
        console.warn('AuthContext - User data refresh failed:', error.message);
      });

      console.log('🔐 [AuthContext] Login process completed successfully (non-blocking operations started)');
      return data.user;
    } catch (error) {
      console.error('🔐 [AuthContext] Login error:', error);

      // New: Enhanced error logging
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('🔐 [AuthContext] Error response data:', error.response.data);
        console.error('🔐 [AuthContext] Error response status:', error.response.status);
        console.error('🔐 [AuthContext] Error response headers:', error.response.headers);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('🔐 [AuthContext] No response received:', error.request);
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('🔐 [AuthContext] Error message:', error.message);
      }
      console.error('🔐 [AuthContext] Full error object:', JSON.stringify(error, Object.getOwnPropertyNames(error), 2));


      // Handle specific Firebase network errors
      if (error.message && error.message.includes('auth/network-request-failed')) {
        throw new Error('Network connection failed. Please check your internet connection and try again.');
      }

      // Handle other Firebase auth errors
      if (error.code) {
        switch (error.code) {
          case 'auth/user-not-found':
            throw new Error('No account found with this email address.');
          case 'auth/wrong-password':
            throw new Error('Incorrect password. Please try again.');
          case 'auth/invalid-email':
            throw new Error('Please enter a valid email address.');
          case 'auth/user-disabled':
            throw new Error('This account has been disabled.');
          case 'auth/too-many-requests':
            throw new Error('Too many failed login attempts. Please try again later.');
          default:
            throw new Error('Login failed. Please try again.');
        }
      }

      // New: Improved backend error handling
      if (error.response && error.response.data && error.response.data.message) {
        throw new Error(error.response.data.message);
      }

      // Handle backend errors
      if (error.response) {
        const { status, data } = error.response;
        if (status === 401) {
          throw new Error(data.message || 'Authentication failed. Please check your credentials.');
        } else if (status >= 500) {
          throw new Error('A server error occurred. Please try again later.');
        }
      }

      throw new Error('Login failed. Please try again.');
    }
  };

  const loginWithFacebook = async () => {
    try {
      console.log('AuthContext - Facebook login initiated');
      const data = await apiLoginWithFacebook();
      console.log('AuthContext - Facebook login response:', data);
      console.log('AuthContext - Facebook login user data:', data.user);
      console.log('AuthContext - Facebook login private code:', data.user?.privateCode);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);

      // Generate and set affiliate link after successful login
      const affiliateRes = await generateAffiliateLink(data.user.id);
      if (affiliateRes.success) {
        setAffiliateLink(affiliateRes.affiliateLink);
        // Update user in localStorage with affiliate link
        const updatedUserWithAffiliate = { ...data.user, affiliateLink: affiliateRes.affiliateLink };
        localStorage.setItem('user', JSON.stringify(updatedUserWithAffiliate));
        setUser(updatedUserWithAffiliate);
      } else {
        console.warn('AuthContext - Failed to generate affiliate link after Facebook login:', affiliateRes.message);
      }

      // Refresh user data after login to ensure latest info
      await refreshUserData();

      return data.user;
    } catch (error) {
      console.error('AuthContext - Facebook login error:', error);

      // Handle specific Firebase auth errors for Facebook
      if (error.code) {
        switch (error.code) {
          case 'auth/popup-closed-by-user':
            throw new Error('Facebook login was cancelled.');
          case 'auth/popup-blocked':
            throw new Error('Popup was blocked by browser. Please allow popups and try again.');
          case 'auth/cancelled-popup-request':
            throw new Error('Facebook login was cancelled.');
          case 'auth/account-exists-with-different-credential':
            throw new Error('An account already exists with this email address using a different sign-in method.');
          default:
            throw new Error('Facebook login failed. Please try again.');
        }
      }

      // Handle backend errors
      if (error.response && error.response.status === 401) {
        throw new Error('Facebook authentication failed. Please try again.');
      }

      // Re-throw the error if it's already user-friendly
      throw error;
    }
  };

  // GitHub login function
  const loginWithGithub = async () => {
    try {
      console.log('AuthContext - GitHub login initiated');
      const data = await apiLoginWithGithub();
      console.log('AuthContext - GitHub login response:', data);
      console.log('AuthContext - GitHub login user data:', data.user);
      console.log('AuthContext - GitHub login private code:', data.user?.privateCode);
      localStorage.setItem('token', data.token);
      localStorage.setItem('user', JSON.stringify(data.user));
      setUser(data.user);

      // Generate and set affiliate link after successful login
      const affiliateRes = await generateAffiliateLink(data.user.id);
      if (affiliateRes.success) {
        setAffiliateLink(affiliateRes.affiliateLink);
        // Update user in localStorage with affiliate link
        const updatedUserWithAffiliate = { ...data.user, affiliateLink: affiliateRes.affiliateLink };
        localStorage.setItem('user', JSON.stringify(updatedUserWithAffiliate));
        setUser(updatedUserWithAffiliate);
      } else {
        console.warn('AuthContext - Failed to generate affiliate link after GitHub login:', affiliateRes.message);
      }

      // Refresh user data after login to ensure latest info
      await refreshUserData();

      return data.user;
    } catch (error) {
      console.error('AuthContext - GitHub login error:', error);

      // Handle specific Firebase auth errors for GitHub
      if (error.code) {
        switch (error.code) {
          case 'auth/popup-closed-by-user':
            throw new Error('GitHub login was cancelled.');
          case 'auth/popup-blocked':
            throw new Error('Popup was blocked by browser. Please allow popups and try again.');
          case 'auth/cancelled-popup-request':
            throw new Error('GitHub login was cancelled.');
          case 'auth/account-exists-with-different-credential':
            throw new Error('An account already exists with this email address using a different sign-in method.');
          default:
            throw new Error('GitHub login failed. Please try again.');
        }
      }

      // Handle backend errors
      if (error.response && error.response.status === 401) {
        throw new Error('GitHub authentication failed. Please try again.');
      }

      // Re-throw the error if it's already user-friendly
      throw error;
    }
  };

  // Register function
  const register = async (userData) => {
    try {
      // Check for stored affiliate referral data
      const storedReferral = localStorage.getItem('affiliateReferral');
      let referralData = null;
      if (storedReferral) {
        try {
          referralData = JSON.parse(storedReferral);
          // Check if referral data is not too old (24 hours)
          if (Date.now() - referralData.timestamp > 24 * 60 * 60 * 1000) {
            localStorage.removeItem('affiliateReferral');
            referralData = null;
          }
        } catch (error) {
          console.warn('AuthContext - Invalid referral data in localStorage:', error);
          localStorage.removeItem('affiliateReferral');
        }
      }

      // Include referral data in registration if available
      const registrationData = { ...userData };
      if (referralData) {
        registrationData.referralRef = referralData.ref;
        registrationData.referralPrivateCode = referralData.privateCode;
        console.log('AuthContext - Including affiliate referral data in registration:', referralData);
      }

      const result = await registerUserWithFirebase(registrationData);
      if (!result.success) {
        throw new Error(result.message || result.error || 'Registration failed');
      }
      console.log('AuthContext - Register response:', result);
      console.log('AuthContext - Register user data:', result.user);
      console.log('AuthContext - Register private code:', result.user?.privateCode);
      localStorage.setItem('token', result.token);
      localStorage.setItem('user', JSON.stringify(result.user));
      setUser(result.user);

      // Clear stored referral data after successful registration
      if (referralData) {
        localStorage.removeItem('affiliateReferral');
      }

      // Generate and set affiliate link after successful registration
      const affiliateRes = await generateAffiliateLink(result.user.id);
      if (affiliateRes.success) {
        setAffiliateLink(affiliateRes.affiliateLink);
        // Update user in localStorage with affiliate link
        const updatedUserWithAffiliate = { ...result.user, affiliateLink: affiliateRes.affiliateLink };
        localStorage.setItem('user', JSON.stringify(updatedUserWithAffiliate));
        setUser(updatedUserWithAffiliate);
      } else {
        console.warn('AuthContext - Failed to generate affiliate link after registration:', affiliateRes.message);
      }

      return result.user;
    } catch (error) {
      console.error('Registration error:', error);
      throw error;
    }
  };

  // Sign in anonymously
  const signInAnonymouslyUser = async () => {
    try {
      console.log('AuthContext - Attempting anonymous sign-in');
      await signInAnonymously(auth);
      console.log('AuthContext - Anonymous sign-in initiated');
      // The onAuthStateChanged listener will handle the rest
    } catch (error) {
      console.error('AuthContext - Anonymous sign-in error:', error);
      throw error;
    }
  };

  // Logout function
  const logout = async () => {
    try {
      console.log('AuthContext - Logging out user');
      await signOut(auth); // Sign out from Firebase
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      console.log('AuthContext - User logged out successfully');
    } catch (error) {
      console.error('AuthContext - Logout error:', error);
      // Even if Firebase logout fails, clear local state
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  // Get current auth token
  const getToken = () => {
    return localStorage.getItem('token');
  };

  // Validate token by checking if it's expired or invalid
  const validateToken = async () => {
    const token = getToken();
    if (!token) return false;

    try {
      // Try a simple authenticated request to validate token
      const response = await apiClient.get('/api/auth/validate', {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.status === 200;
    } catch (error) {
      console.warn('Token validation failed:', error.message);
      if (error.response?.status === 401) {
        // Token is invalid/expired, clear it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        return false;
      }
      // For other errors, assume token is still valid
      return true;
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    return user !== null && getToken() !== null;
  };

  // Enhanced authentication check with token validation
  const isAuthenticatedWithValidation = async () => {
    if (!user || !getToken()) return false;
    return await validateToken();
  };

  // Refresh token if needed (check expiration and get new token)
  const refreshTokenIfNeeded = async () => {
    const token = getToken();
    if (!token) return false;

    try {
      // Decode token to check expiration (without verification)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Date.now() / 1000;
      const timeUntilExpiry = payload.exp - currentTime;

      // If token expires in less than 1 hour, try to refresh
      if (timeUntilExpiry < 3600) {
        console.log('Token expires soon, attempting refresh...');
        
        // Try to get a fresh Firebase ID token if user is signed in
        if (auth.currentUser) {
          try {
            const freshIdToken = await auth.currentUser.getIdToken(true); // Force refresh
            
            // Call backend to get new JWT
            const response = await apiClient.post('/api/auth/login-firebase', {}, {
              headers: {
                Authorization: `Bearer ${freshIdToken}`,
                'X-Override-Auth': 'true'
              }
            });

            if (response.data.token) {
              localStorage.setItem('token', response.data.token);
              localStorage.setItem('user', JSON.stringify(response.data.user));
              setUser(response.data.user);
              console.log('Token refreshed successfully');
              return true;
            }
          } catch (refreshError) {
            console.warn('Token refresh failed:', refreshError.message);
            return false;
          }
        }
      }
      return true; // Token is still valid
    } catch (error) {
      console.warn('Error checking token expiration:', error.message);
      return false;
    }
  };

  // Check if user has specific plan
  const hasPlan = (plan) => {
    return user?.planStatus === plan;
  };

  // Refresh user data from backend (non-blocking for faster login)
  const refreshUserData = async () => {
    if (!user?.id) {
      console.log('AuthContext - No user ID found, skipping refresh');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.log('AuthContext - No token found, skipping refresh');
        return;
      }

      console.log('AuthContext - Starting user data refresh for user:', user.id);

      // Get user data and subscription status in parallel for faster execution
      const [userData, subscriptionResponse] = await Promise.allSettled([
        getUserById(user.id).catch(error => {
          console.warn('AuthContext - Failed to fetch user data:', error.message);
          return null;
        }),
        apiClient.get('/api/users/subscription-status/' + user.id).catch(error => {
          console.warn('AuthContext - Failed to fetch subscription status:', error.message);
          return { status: 200, data: {} };
        })
      ]);

      // Extract data from promises
      const freshUserData = userData.status === 'fulfilled' ? userData.value : null;
      const subscriptionData = subscriptionResponse.status === 'fulfilled' ? subscriptionResponse.value.data : {};

      const updatedUser = {
        ...user,
        ...(freshUserData || {}), // Include all user data including privateCode
        planStatus: subscriptionData.planType?.toLowerCase() || user.planStatus || 'free',
        subscriptionStatus: subscriptionData.subscriptionStatus,
        role: subscriptionData.role || user.role
      };

      console.log('AuthContext - Final updated user data:', updatedUser);
      console.log('AuthContext - Final private code:', updatedUser?.privateCode);

      localStorage.setItem('user', JSON.stringify(updatedUser));
      setUser(updatedUser);

      console.log('AuthContext - User data refresh completed successfully');

      // Generate affiliate link asynchronously (non-blocking)
      generateReferralLink(user.id).then(affiliateRes => {
        if (affiliateRes.success) {
          const userWithAffiliate = { ...updatedUser, affiliateLink: affiliateRes.affiliateLink };
          setAffiliateLink(affiliateRes.affiliateLink);
          localStorage.setItem('user', JSON.stringify(userWithAffiliate));
          setUser(userWithAffiliate);
        } else {
          console.warn('AuthContext - Failed to generate affiliate link:', affiliateRes.message);
        }
      }).catch(error => {
        console.warn('AuthContext - Affiliate link generation failed:', error.message);
      });

    } catch (error) {
      console.warn('AuthContext - Error refreshing user data:', error.message);

      // Fallback: try to get privateCode from localStorage and update user context
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser?.privateCode) {
            console.log('AuthContext - Using privateCode from localStorage fallback:', parsedUser.privateCode);
            setUser(prevUser => ({ ...prevUser, privateCode: parsedUser.privateCode }));
          }
        }
      } catch (fallbackError) {
        console.warn('AuthContext - Error parsing stored user data in fallback:', fallbackError.message);
      }
    }
  };

  // Generate referral link with better error handling
  const generateReferralLink = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('AuthContext - No token found for referral generation');
        return { success: false, message: 'No authentication token available' };
      }

      const res = await apiClient.post('/api/referral/generate', { userId }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return res.data;
    } catch (err) {
      console.warn('AuthContext - Referral link generation failed:', err.message);
      // Return a fallback response instead of throwing
      return {
        success: false,
        message: 'Referral link generation failed',
        error: err.message
      };
    }
  };

  // Generate affiliate link
  const generateAffiliateLink = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('AuthContext - No token found for affiliate link generation');
        return { success: false, message: 'No authentication token available' };
      }

      const res = await apiClient.post('/api/referral/affiliate/generate', { userId }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return res.data;
    } catch (err) {
      console.warn('AuthContext - Affiliate link generation failed:', err.message);
      // Return a fallback response instead of throwing
      return {
        success: false,
        message: 'Affiliate link generation failed',
        error: err.message
      };
    }
  };

  // Fetch user referrals
  const fetchUserReferrals = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const res = await apiClient.get(`/api/referral/user/${userId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setReferrals(res.data.data || []);
      return res.data;
    } catch (err) {
      console.error('Failed to fetch referrals:', err);
      throw err;
    }
  };

  // Fetch user commissions
  const fetchUserCommissions = async (userId) => {
    if (!userId) {
      console.warn('fetchUserCommissions called without a valid userId');
      setCommissions([]); // Clear commissions or keep previous state
      return null;
    }

    // Handle mock/anonymous users (development mode)
    if (userId.startsWith('mock-') || userId.startsWith('anonymous-')) {
      console.log('Mock mode detected for commissions fetch - returning mock data');
      const mockCommissions = [
        {
          id: 'mock-comm1',
          referrerId: userId,
          referredId: 'mock-referred1',
          planPrice: 29.99,
          commissionRate: 0.2,
          amount: 5.998,
          status: 'paid',
          createdAt: new Date().toISOString()
        },
        {
          id: 'mock-comm2',
          referrerId: userId,
          referredId: 'mock-referred2',
          planPrice: 99.99,
          commissionRate: 0.3,
          amount: 29.997,
          status: 'pending',
          createdAt: new Date(Date.now() - 86400000).toISOString() // Yesterday
        }
      ];
      setCommissions(mockCommissions);
      return {
        success: true,
        data: {
          commissions: mockCommissions,
          summary: {
            totalEarned: 35.995,
            totalPending: 29.997,
            totalPaid: 5.998,
            totalCommissions: 2
          }
        }
      };
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('No token found for commissions fetch - user may be in mock mode');
        setCommissions([]);
        return null;
      }

      // Get user's privateCode from user data
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      const privateCode = userData.privateCode;

      if (!privateCode) {
        console.warn('No privateCode found for user, cannot fetch commissions');
        setCommissions([]);
        return null;
      }

      const res = await apiClient.get(`/api/referral/user/${userId}/commissions`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        params: {
          privateCode: privateCode
        }
      });
      setCommissions(res.data.data.commissions || []);
      return res.data;
    } catch (err) {
      console.error('Failed to fetch commissions:', err);
      // For 403 errors in development, fallback to mock data
      if (err.response?.status === 403 && process.env.NODE_ENV === 'development') {
        console.warn('403 error in development - falling back to mock commissions data');
        const mockCommissions = [
          {
            id: 'mock-comm1',
            referrerId: userId,
            referredId: 'mock-referred1',
            planPrice: 29.99,
            commissionRate: 0.2,
            amount: 5.998,
            status: 'paid',
            createdAt: new Date().toISOString()
          }
        ];
        setCommissions(mockCommissions);
        return {
          success: true,
          data: {
            commissions: mockCommissions,
            summary: {
              totalEarned: 5.998,
              totalPending: 0,
              totalPaid: 5.998,
              totalCommissions: 1
            }
          }
        };
      }
      throw err;
    }
  };

  // Request payout
  const requestPayout = async (userId, payoutMethod, payoutDetails) => {
    try {
      const token = localStorage.getItem('token');
      const res = await apiClient.post(`/api/referral/user/${userId}/payout`, {
        payoutMethod,
        payoutDetails
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      return res.data;
    } catch (err) {
      console.error('Payout request failed:', err);
      throw err;
    }
  };

  // Account Linking Functions
  const linkGoogleAccount = async () => {
    try {
      if (!user) {
        throw new Error('No user is currently signed in');
      }

      const firebaseUser = auth.currentUser;

      if (!firebaseUser) {
        throw new Error('Firebase user not found');
      }

      await accountLinkingService.linkGoogleAccount(firebaseUser);
    } catch (error) {
      console.error('Error linking Google account:', error);
      throw error;
    }
  };

  const handleGoogleRedirectResult = async () => {
    try {
      const result = await accountLinkingService.handleRedirectResult();

      if (result.success && result.user) {
        // Refresh user data to get updated profile
        await refreshUserData();
      }

      return result;
    } catch (error) {
      console.error('Error handling Google redirect result:', error);
      throw error;
    }
  };

  const signInWithGoogle = async () => {
    try {
      await accountLinkingService.signInWithGoogle();
    } catch (error) {
      console.error('Error signing in with Google:', error);
      throw error;
    }
  };

  const isGoogleLinked = () => {
    if (!user) return false;
    return accountLinkingService.isGoogleLinked(user);
  };

  const getLinkedProviders = () => {
    if (!user) return [];
    return accountLinkingService.getLinkedProviders(user);
  };

  const value = {
    user,
    loading,
    login,
    loginWithFacebook,
    loginWithGithub,
    register,
    signInAnonymouslyUser,
    logout,
    isAuthenticated,
    isAuthenticatedWithValidation,
    validateToken,
    refreshTokenIfNeeded,
    hasPlan,
    setUser,
    refreshUserData,
    generateReferralLink,
    generateAffiliateLink,
    fetchUserReferrals,
    fetchUserCommissions,
    requestPayout,
    referrals,
    commissions,
    affiliateLink, // Expose affiliateLink
    getToken, // Expose getToken function
    tokenBalance,
    setTokenBalance,
    walletBalance,
    setWalletBalance,
    // Account linking functions
    linkGoogleAccount,
    handleGoogleRedirectResult,
    signInWithGoogle,
    isGoogleLinked,
    getLinkedProviders
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
